import {
  TextField,
  SelectInput,
  TopToolbar,
  FilterButton,
  FunctionField,
  WrapperField,
  useTranslate,
  useLocaleState,
  RaRecord,
  ReferenceInput,
} from "react-admin";

import { ListGuesser, FieldGuesser } from "@api-platform/admin";
import { useMediaQuery, Theme, IconButton } from "@mui/material";

import PauseCircleIcon from "@mui/icons-material/PauseCircleFilled";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import AnalysisStatusField from "shared/components/AnalysisStatusField";
import { CustomActionButtonWithConfirm } from "shared/components/CustomActionButton";
import CustomActionLink from "shared/components/CustomActionLink";
import { runAnalysis, stopAnalysis } from "lib/client/api";
import ETAField from "shared/components/ETAField";
import DurationField from "shared/components/DurationField";
import DateFieldUTC from "shared/components/DateFieldUTC";
import AcquisitionsField from "shared/components/AcquisitionsField";
import queryClient from "lib/client/main";

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
  </TopToolbar>
);

const REFERTCH_INTERVAL = 15000;
function AnalysisList() {
  const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const t = useTranslate();
  const [locale] = useLocaleState();

  const analysesListFilters = [
    <SelectInput
      source="status"
      label="resources.analyses.filter.status"
      choices={[
        { id: "done", name: t("resources.status.done") },
        { id: "error", name: t("resources.status.error") },
        { id: "cached", name: t("resources.status.cached") },
        { id: "scheduled", name: t("resources.status.scheduled") },
        { id: "pending", name: t("resources.status.pending") },
        { id: "running", name: t("resources.status.running") },
        { id: "aborted", name: t("resources.status.aborted") },
        { id: "notrun", name: t("resources.status.notrun") },
      ]}
      emptyText="resources.analyses.filter.status.empty"
      emptyValue={undefined}
      alwaysOn
    />,
    <ReferenceInput
      source="group"
      reference="analyzis_groups"
      label="resources.patients.filter.groups"
    >
      <SelectInput
        label="resources.patients.filter.groups"
        emptyText="resources.patients.filter.groups.empty"
        emptyValue={undefined}
        optionText="name"
      />
    </ReferenceInput>,
  ];

  function getAnalysisURL(record: RaRecord) {
    if (!record.acquisitions?.[0]) return "";
    const patientId = record.patient_id;
    const studyId = record.study_uid;
    const acquisition = record.acquisitions[0];
    if (!["done", "cached"].includes(record.status)) return "";
    return `/patients/${patientId}/studies/${studyId}?exam_date=${acquisition}`;
  }

  return (
    <ListGuesser
      actions={<ListActions />}
      bulkActionButtons={false}
      hasShow={false}
      hasEdit={false}
      filters={analysesListFilters}
      rowClick={(id, resource, record: RaRecord) => getAnalysisURL(record)}
      rowStyle={(record) =>
        !["done", "cached"].includes(record.status)
          ? { cursor: "default" }
          : null
      }
      sort={{ order: "DESC", field: "id" }}
      queryOptions={{ refetchInterval: REFERTCH_INTERVAL }}
    >
      <FieldGuesser source="id" />
      {isLarge ? <TextField source="group_name" /> : null}
      {isLarge ? <TextField source="patient_uid" /> : null}
      <AcquisitionsField
        source="acquisitions"
        sortable={false}
        locales={locale}
        options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
      />
      {isLarge ? (
        <DateFieldUTC
          source="started_at"
          locales={locale}
          options={{
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "numeric",
            minute: "numeric",
            hour12: false,
          }}
        />
      ) : null}
      {isLarge ? <DurationField source="duration" /> : null}
      <ETAField source="eta" sortable={false} />
      {isLarge ? (
        <FunctionField
          source="current_step"
          sortable={false}
          render={getCurrentorErrorStep}
        />
      ) : null}
      <AnalysisStatusField source="status" textAlign="center" />
      <WrapperField
        cellClassName="!text-right"
        headerClassName="!text-right"
        label="Actions"
      >
        <CustomActionButtonWithConfirm
          title={t("resources.analyses.tooltip.rerun")}
          aria-label={t("resources.analyses.tooltip.rerun")}
          confirmTitle={t("resources.analyses.tooltip.rerun")}
          confirmContent={t("resources.analyses.tooltip.rerun.confirm")}
          callback={(record) => {
            runAnalysis(record.id as number);
            queryClient.invalidateQueries(["analyses"]);
          }}
          isHidden={(record) =>
            !["error", "done", "cached", "aborted"].includes(record.status)
          }
          icon={<RestartAltIcon />}
        />
        <CustomActionButtonWithConfirm
          title={t("resources.analyses.tooltip.stop")}
          aria-label={t("resources.analyses.tooltip.stop")}
          confirmTitle={t("resources.analyses.tooltip.stop")}
          confirmContent={t("resources.analyses.tooltip.stop.confirm")}
          callback={(record) => {
            stopAnalysis(record.id as number);
            queryClient.invalidateQueries(["analyses"]);
          }}
          isHidden={(record) =>
            ["done", "cached", "error", "aborted"].includes(record.status)
          }
          icon={<PauseCircleIcon />}
        />
        <CustomActionLink
          title={t("resources.analyses.tooltip.view")}
          aria-label={t("resources.analyses.tooltip.view")}
          getPath={(record) => getAnalysisURL(record)}
          isHidden={(record) => !["done", "cached"].includes(record.status)}
        >
          <IconButton size="small">
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </CustomActionLink>
      </WrapperField>
    </ListGuesser>
  );

  function getCurrentorErrorStep({ steps }) {
    return (
      steps
        ?.find((step) => step.status === "running")
        ?.name?.replaceAll("_", " ") ||
      steps?.find((step) => step.status === "error")?.name?.replaceAll("_", " ")
    );
  }
}

export default AnalysisList;
